export const CLIENT_PERFORMANCE_REPORT_INTERVAL_MS = 60_000;

export interface PresentationPerformanceSample {
  renderWorkMs: number;
  snapshotQueueMs: number;
  missedDeadlines: number;
}

export interface ClientPerformanceReport {
  sampleWindowMs: number;
  frameCount: number;
  averageFrameMs: number;
  p95FrameMs: number;
  maxFrameMs?: number;
  longFrameCount: number;
  framesOver100Ms?: number;
  snapshotCount: number;
  averageSnapshotGapMs: number;
  maxSnapshotGapMs: number;
  averageSnapshotWorkMs?: number;
  p95SnapshotWorkMs?: number;
  maxSnapshotWorkMs?: number;
  presentationCount?: number;
  averagePresentationWorkMs?: number;
  p95PresentationWorkMs?: number;
  maxPresentationWorkMs?: number;
  averageSnapshotQueueMs?: number;
  p95SnapshotQueueMs?: number;
  maxSnapshotQueueMs?: number;
  missedPresentationDeadlines?: number;
}

/**
 * Candidate TV quality gate. It is intentionally one-way at the caller: a slow window may remove
 * optional effects, while restoring them still requires a fresh representative benchmark.
 */
export function exceedsTvPresentationBudget(report: ClientPerformanceReport): boolean {
  if (report.frameCount < 30) return false;
  const cadenceSlow =
    report.p95FrameMs > 40 || report.longFrameCount / Math.max(1, report.frameCount) > 0.05;
  const presentationCount = report.presentationCount ?? 0;
  const presentationSlow =
    presentationCount >= 30 &&
    ((report.p95PresentationWorkMs ?? 0) > 28 ||
      (report.p95SnapshotQueueMs ?? 0) > 100 ||
      (report.missedPresentationDeadlines ?? 0) / presentationCount > 0.05);
  return cadenceSlow || presentationSlow;
}

export class ClientPerformanceSampler {
  private active = false;
  private windowStartedAt = 0;
  private lastFrameAt = 0;
  private lastSnapshotAt = 0;
  private frameDurations: number[] = [];
  private snapshotGaps: number[] = [];
  private snapshotWorkDurations: number[] = [];
  private presentationWorkDurations: number[] = [];
  private snapshotQueueDurations: number[] = [];
  private missedPresentationDeadlines = 0;
  private snapshotCount = 0;

  setActive(active: boolean, nowMs: number): void {
    if (active === this.active) return;
    this.active = active;
    this.reset(nowMs);
  }

  isActive(): boolean {
    return this.active;
  }

  observeFrame(nowMs: number): void {
    if (!this.active) return;
    if (this.lastFrameAt > 0) this.frameDurations.push(nowMs - this.lastFrameAt);
    this.lastFrameAt = nowMs;
  }

  observeSnapshot(nowMs: number): void {
    if (!this.active) return;
    if (this.lastSnapshotAt > 0) this.snapshotGaps.push(nowMs - this.lastSnapshotAt);
    this.lastSnapshotAt = nowMs;
    this.snapshotCount += 1;
  }

  observeSnapshotWork(durationMs: number): void {
    if (!this.active || !Number.isFinite(durationMs) || durationMs < 0) return;
    this.snapshotWorkDurations.push(durationMs);
  }

  observePresentation(sample: PresentationPerformanceSample): void {
    if (
      !this.active ||
      !Number.isFinite(sample.renderWorkMs) ||
      sample.renderWorkMs < 0 ||
      !Number.isFinite(sample.snapshotQueueMs) ||
      sample.snapshotQueueMs < 0 ||
      !Number.isSafeInteger(sample.missedDeadlines) ||
      sample.missedDeadlines < 0
    ) {
      return;
    }
    this.presentationWorkDurations.push(sample.renderWorkMs);
    this.snapshotQueueDurations.push(sample.snapshotQueueMs);
    this.missedPresentationDeadlines += sample.missedDeadlines;
  }

  takeReport(nowMs: number): ClientPerformanceReport | null {
    if (
      !this.active ||
      nowMs - this.windowStartedAt < CLIENT_PERFORMANCE_REPORT_INTERVAL_MS ||
      this.frameDurations.length === 0
    ) {
      return null;
    }
    const sortedFrames = [...this.frameDurations].sort((a, b) => a - b);
    const sortedSnapshotWork = [...this.snapshotWorkDurations].sort((a, b) => a - b);
    const sortedPresentationWork = [...this.presentationWorkDurations].sort((a, b) => a - b);
    const sortedSnapshotQueue = [...this.snapshotQueueDurations].sort((a, b) => a - b);
    const report: ClientPerformanceReport = {
      sampleWindowMs: Math.min(10 * 60_000, Math.round(nowMs - this.windowStartedAt)),
      frameCount: this.frameDurations.length,
      averageFrameMs: round(clamp(average(this.frameDurations), 0, 10_000)),
      p95FrameMs: round(clamp(percentile(sortedFrames, 0.95), 0, 10_000)),
      maxFrameMs: round(clamp(Math.max(0, ...this.frameDurations), 0, 10_000)),
      longFrameCount: this.frameDurations.filter((duration) => duration > 40).length,
      framesOver100Ms: this.frameDurations.filter((duration) => duration >= 100).length,
      snapshotCount: this.snapshotCount,
      averageSnapshotGapMs: round(clamp(average(this.snapshotGaps), 0, 60_000)),
      maxSnapshotGapMs: round(clamp(Math.max(0, ...this.snapshotGaps), 0, 60_000)),
      averageSnapshotWorkMs: round(clamp(average(this.snapshotWorkDurations), 0, 10_000)),
      p95SnapshotWorkMs: round(clamp(percentile(sortedSnapshotWork, 0.95), 0, 10_000)),
      maxSnapshotWorkMs: round(clamp(Math.max(0, ...this.snapshotWorkDurations), 0, 10_000)),
      presentationCount: this.presentationWorkDurations.length,
      averagePresentationWorkMs: round(clamp(average(this.presentationWorkDurations), 0, 10_000)),
      p95PresentationWorkMs: round(clamp(percentile(sortedPresentationWork, 0.95), 0, 10_000)),
      maxPresentationWorkMs: round(
        clamp(Math.max(0, ...this.presentationWorkDurations), 0, 10_000),
      ),
      averageSnapshotQueueMs: round(clamp(average(this.snapshotQueueDurations), 0, 10_000)),
      p95SnapshotQueueMs: round(clamp(percentile(sortedSnapshotQueue, 0.95), 0, 10_000)),
      maxSnapshotQueueMs: round(clamp(Math.max(0, ...this.snapshotQueueDurations), 0, 10_000)),
      missedPresentationDeadlines: Math.min(100_000, this.missedPresentationDeadlines),
    };
    this.reset(nowMs);
    return report;
  }

  private reset(nowMs: number): void {
    this.windowStartedAt = nowMs;
    this.lastFrameAt = 0;
    this.lastSnapshotAt = 0;
    this.frameDurations = [];
    this.snapshotGaps = [];
    this.snapshotWorkDurations = [];
    this.presentationWorkDurations = [];
    this.snapshotQueueDurations = [];
    this.missedPresentationDeadlines = 0;
    this.snapshotCount = 0;
  }
}

export function isClientPerformanceReport(value: unknown): value is ClientPerformanceReport {
  if (!isRecord(value)) return false;
  return (
    boundedNumber(value.sampleWindowMs, 1_000, 10 * 60_000) &&
    boundedInteger(value.frameCount, 0, 100_000) &&
    boundedNumber(value.averageFrameMs, 0, 10_000) &&
    boundedNumber(value.p95FrameMs, 0, 10_000) &&
    optionalBoundedNumber(value.maxFrameMs, 0, 10_000) &&
    boundedInteger(value.longFrameCount, 0, 100_000) &&
    optionalBoundedInteger(value.framesOver100Ms, 0, 100_000) &&
    boundedInteger(value.snapshotCount, 0, 100_000) &&
    boundedNumber(value.averageSnapshotGapMs, 0, 60_000) &&
    boundedNumber(value.maxSnapshotGapMs, 0, 60_000) &&
    optionalBoundedNumber(value.averageSnapshotWorkMs, 0, 10_000) &&
    optionalBoundedNumber(value.p95SnapshotWorkMs, 0, 10_000) &&
    optionalBoundedNumber(value.maxSnapshotWorkMs, 0, 10_000) &&
    optionalBoundedInteger(value.presentationCount, 0, 100_000) &&
    optionalBoundedNumber(value.averagePresentationWorkMs, 0, 10_000) &&
    optionalBoundedNumber(value.p95PresentationWorkMs, 0, 10_000) &&
    optionalBoundedNumber(value.maxPresentationWorkMs, 0, 10_000) &&
    optionalBoundedNumber(value.averageSnapshotQueueMs, 0, 10_000) &&
    optionalBoundedNumber(value.p95SnapshotQueueMs, 0, 10_000) &&
    optionalBoundedNumber(value.maxSnapshotQueueMs, 0, 10_000) &&
    optionalBoundedInteger(value.missedPresentationDeadlines, 0, 100_000)
  );
}

function average(values: readonly number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function percentile(sorted: readonly number[], ratio: number): number {
  if (sorted.length === 0) return 0;
  return sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * ratio))] ?? 0;
}

function round(value: number): number {
  return Math.round(value * 10) / 10;
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}

function boundedNumber(value: unknown, minimum: number, maximum: number): boolean {
  return (
    typeof value === "number" && Number.isFinite(value) && value >= minimum && value <= maximum
  );
}

function boundedInteger(value: unknown, minimum: number, maximum: number): boolean {
  return Number.isSafeInteger(value) && boundedNumber(value, minimum, maximum);
}

function optionalBoundedNumber(value: unknown, minimum: number, maximum: number): boolean {
  return value === undefined || boundedNumber(value, minimum, maximum);
}

function optionalBoundedInteger(value: unknown, minimum: number, maximum: number): boolean {
  return value === undefined || boundedInteger(value, minimum, maximum);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
